import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { LocalStorageID, LocalStorageStateService } from '@stylehaven/components';
import { ApiFragment } from '../record/entities/api-fragment.entity';
import { ColumnDef } from '../record/entities/column-definition.entity';
import { MetadataField } from '../record/entities/metadata-field.entity';
import { ColumnConfigureOptions, ColumnPickerGroup } from './column-picker-group.entity';
import { ListViewStateService } from './column-management-vendor-access';

/** "Toon submappen" is opt-in per level and defaults to on, matching the grouping behaviour it toggles. */
const DEFAULT_SHOW_SUBFOLDERS = true;

/**
 * Keys that can never be visible together — selecting one deselects the other. Only edepot's
 * "Naam en serie" (archive overview) vs. plain "Naam" (records overview) pair exists today; the
 * two never appear in the same container's `allColumns`, so this guard is currently a no-op for
 * every configured overview (see HFBD-11 issue 002). The archive-overview key is edepot's
 * `CustomDossierColumns.DossierAndSeriesValidityPeriod` — duplicated here as a string literal
 * because `digihaven-shared` cannot depend on an app-level entity. `MetadataField.Title` lives in
 * this same lib, so that side of the pair is referenced directly rather than duplicated.
 */
const MUTUALLY_EXCLUSIVE_COLUMN_KEYS: ReadonlyArray<readonly [string, string]> = [
    ['DossierAndSeriesValidityPeriod', MetadataField.Title]
];

interface OverviewColumnState {
    readonly allColumns: ColumnDef<ApiFragment>[];
    readonly defaultKeys: string[];
    readonly label?: string;
    readonly pinnedKeys: readonly string[];
    readonly pendingKeys$: BehaviorSubject<string[]>;
    readonly activeColumns$: BehaviorSubject<ColumnDef<ApiFragment>[]>;
    readonly showSubfoldersToggle: boolean;
    readonly pendingShowSubfolders$: BehaviorSubject<boolean>;
    readonly activeShowSubfolders$: BehaviorSubject<boolean>;
}

/**
 * Centralizes per-overview column configuration and state for the 5 edepot overview lists, so no
 * container duplicates column-picker wiring, persistence, or the name-exclusivity guard.
 * Persistence is fully delegated to the vendor `ListViewStateService` — this service only adds an
 * in-memory staging step (stage → apply/discard) and the invariants around it.
 */
@Injectable({ providedIn: 'root' })
export class ColumnManagementService {
    private readonly listViewStateService: ListViewStateService = inject(ListViewStateService);
    private readonly localStorageStateService: LocalStorageStateService = inject(LocalStorageStateService);
    private readonly overviews = new Map<string, OverviewColumnState>();

    /** Mirrors `ListViewStateService`'s own pattern, but keyed by overviewId → a plain boolean, not columns. */
    private readonly showSubfoldersStorage = signal<Record<string, boolean>>({});

    constructor() {
        this.localStorageStateService.isInitialized$
            .pipe(
                filter(isInitialized => isInitialized),
                take(1)
            )
            .subscribe(() => {
                const storedState: Record<string, boolean> | null = this.localStorageStateService.getState(
                    LocalStorageID.ShowSubfolders
                );

                if (storedState) {
                    this.showSubfoldersStorage.set(storedState);
                }
            });
    }

    configure(
        overviewId: string,
        allColumns: ColumnDef<ApiFragment>[],
        defaultKeys: string[],
        options: ColumnConfigureOptions = {}
    ): void {
        const pinnedKeys: readonly string[] = options.pinnedKeys ?? [];
        const showSubfoldersToggle: boolean = options.showSubfoldersToggle ?? false;
        const initialColumns: ColumnDef<ApiFragment>[] = this.resolveActiveColumns(
            overviewId,
            allColumns,
            defaultKeys,
            pinnedKeys
        );
        const initialShowSubfolders: boolean = this.resolveShowSubfolders(overviewId);

        this.overviews.set(overviewId, {
            allColumns,
            defaultKeys,
            label: options.label,
            pinnedKeys,
            pendingKeys$: new BehaviorSubject<string[]>(initialColumns.map(column => column.toString())),
            activeColumns$: new BehaviorSubject<ColumnDef<ApiFragment>[]>(initialColumns),
            showSubfoldersToggle,
            pendingShowSubfolders$: new BehaviorSubject<boolean>(initialShowSubfolders),
            activeShowSubfolders$: new BehaviorSubject<boolean>(initialShowSubfolders)
        });

        // ListViewStateService only has real data to read once its own localStorage state has
        // loaded, which happens behind an async getCurrentUser() call — on a fresh page load,
        // configure() above typically runs before that resolves, so it falls back to defaultKeys
        // even when a selection was actually saved. Re-resolve once loading genuinely completes.
        this.localStorageStateService.isInitialized$
            .pipe(
                filter(isInitialized => isInitialized),
                take(1)
            )
            .subscribe(() => this.reapplyPersistedState(overviewId));
    }

    private reapplyPersistedState(overviewId: string): void {
        const state: OverviewColumnState | undefined = this.overviews.get(overviewId);

        if (!state) {
            return;
        }

        const columns: ColumnDef<ApiFragment>[] = this.resolveActiveColumns(
            overviewId,
            state.allColumns,
            state.defaultKeys,
            state.pinnedKeys
        );

        state.activeColumns$.next(columns);
        state.pendingKeys$.next(columns.map(column => column.toString()));

        if (state.showSubfoldersToggle) {
            const showSubfolders: boolean = this.resolveShowSubfolders(overviewId);

            state.activeShowSubfolders$.next(showSubfolders);
            state.pendingShowSubfolders$.next(showSubfolders);
        }
    }

    private resolveShowSubfolders(overviewId: string): boolean {
        return this.showSubfoldersStorage()[overviewId] ?? DEFAULT_SHOW_SUBFOLDERS;
    }

    private persistShowSubfolders(overviewId: string, showSubfolders: boolean): void {
        this.showSubfoldersStorage.update(current => ({ ...current, [overviewId]: showSubfolders }));
        this.localStorageStateService.setState(LocalStorageID.ShowSubfolders, this.showSubfoldersStorage());
    }

    private resolveActiveColumns(
        overviewId: string,
        allColumns: ColumnDef<ApiFragment>[],
        defaultKeys: string[],
        pinnedKeys: readonly string[]
    ): ColumnDef<ApiFragment>[] {
        const persistedKeys: string[] | undefined = this.listViewStateService.getListViewState(overviewId);
        const activeColumns: ColumnDef<ApiFragment>[] = persistedKeys
            ? this.keysToColumns(persistedKeys, allColumns)
            : [];
        const resolved: ColumnDef<ApiFragment>[] =
            activeColumns.length > 0 ? activeColumns : this.resolveDefaultColumns(overviewId, defaultKeys, allColumns);

        return this.keysToColumns(
            this.ensurePinnedKeys(
                resolved.map(column => column.toString()),
                allColumns,
                pinnedKeys
            ),
            allColumns
        );
    }

    isConfigured(overviewId: string): boolean {
        return this.overviews.has(overviewId);
    }

    getActiveColumns$(overviewId: string): Observable<ColumnDef<ApiFragment>[]> {
        return this.requireState(overviewId)?.activeColumns$.asObservable() ?? of([]);
    }

    /** Whether the rows this level renders should currently be grouped by folder. Not a column. */
    getShowSubfolders$(overviewId: string): Observable<boolean> {
        return this.overviews.get(overviewId)?.activeShowSubfolders$.asObservable() ?? of(DEFAULT_SHOW_SUBFOLDERS);
    }

    /**
     * The level's picker state, tracking its active columns. `null` for an unconfigured overview,
     * which is how a container asks whether a level has a picker at all.
     */
    getPickerGroup$(overviewId: string): Observable<ColumnPickerGroup | null> {
        const state: OverviewColumnState | undefined = this.overviews.get(overviewId);

        if (!state) {
            return of(null);
        }

        return combineLatest([state.activeColumns$, state.activeShowSubfolders$]).pipe(
            map(([activeColumns, showSubfolders]) => ({
                id: overviewId,
                label: state.label,
                options: state.allColumns.map(column => ({ label: column.label, value: column.toString() })),
                selectedKeys: activeColumns.map(column => column.toString()),
                pinnedKeys: state.pinnedKeys,
                showSubfolders: state.showSubfoldersToggle ? showSubfolders : undefined
            }))
        );
    }

    stageColumns(overviewId: string, selectedKeys: string[]): void {
        const state: OverviewColumnState | undefined = this.requireState(overviewId);

        if (!state) {
            return;
        }

        const guardedKeys: string[] = this.ensurePinnedKeys(
            this.applyNameExclusivityGuard(selectedKeys, state.pendingKeys$.value, state.allColumns),
            state.allColumns,
            state.pinnedKeys
        );

        if (guardedKeys.length === 0) {
            console.warn(`ColumnManagementService: refusing to stage an empty column selection for "${overviewId}".`);
            return;
        }

        state.pendingKeys$.next(guardedKeys);
    }

    /** Stages a "Toon submappen" toggle for the level — a no-op for a level that carries no such toggle. */
    stageShowSubfolders(overviewId: string, showSubfolders: boolean): void {
        const state: OverviewColumnState | undefined = this.requireState(overviewId);

        if (!state || !state.showSubfoldersToggle) {
            return;
        }

        state.pendingShowSubfolders$.next(showSubfolders);
    }

    applyColumns(overviewId: string): void {
        const state: OverviewColumnState | undefined = this.requireState(overviewId);

        if (!state) {
            return;
        }

        const keys: string[] = state.pendingKeys$.value;

        this.listViewStateService.setListViewState(overviewId, keys);
        state.activeColumns$.next(this.keysToColumns(keys, state.allColumns));

        if (state.showSubfoldersToggle) {
            const showSubfolders: boolean = state.pendingShowSubfolders$.value;

            this.persistShowSubfolders(overviewId, showSubfolders);
            state.activeShowSubfolders$.next(showSubfolders);
        }
    }

    discardPendingChanges(overviewId: string): void {
        const state: OverviewColumnState | undefined = this.requireState(overviewId);

        if (!state) {
            return;
        }

        state.pendingKeys$.next(state.activeColumns$.value.map(column => column.toString()));
        state.pendingShowSubfolders$.next(state.activeShowSubfolders$.value);
    }

    resetColumns(overviewId: string): void {
        const state: OverviewColumnState | undefined = this.requireState(overviewId);

        if (!state) {
            return;
        }

        const defaultColumns: ColumnDef<ApiFragment>[] = this.resolveDefaultColumns(
            overviewId,
            state.defaultKeys,
            state.allColumns
        );
        const defaultKeys: string[] = this.ensurePinnedKeys(
            defaultColumns.map(column => column.toString()),
            state.allColumns,
            state.pinnedKeys
        );

        this.listViewStateService.setListViewState(overviewId, defaultKeys);
        state.activeColumns$.next(this.keysToColumns(defaultKeys, state.allColumns));
        state.pendingKeys$.next(defaultKeys);

        if (state.showSubfoldersToggle) {
            this.persistShowSubfolders(overviewId, DEFAULT_SHOW_SUBFOLDERS);
            state.activeShowSubfolders$.next(DEFAULT_SHOW_SUBFOLDERS);
            state.pendingShowSubfolders$.next(DEFAULT_SHOW_SUBFOLDERS);
        }
    }

    private requireState(overviewId: string): OverviewColumnState | undefined {
        const state: OverviewColumnState | undefined = this.overviews.get(overviewId);

        if (!state) {
            console.warn(`ColumnManagementService: "${overviewId}" is not configured. Call configure() first.`);
        }

        return state;
    }

    private keysToColumns(keys: string[], allColumns: ColumnDef<ApiFragment>[]): ColumnDef<ApiFragment>[] {
        return keys
            .map(key => allColumns.find(column => column.toString() === key))
            .filter((column): column is ColumnDef<ApiFragment> => column !== undefined);
    }

    /**
     * Resolves `defaultKeys` against `allColumns`, guaranteeing a non-empty result (as long as
     * `allColumns` itself isn't empty) so `configure()`/`resetColumns()` can never leave an
     * overview with zero visible columns due to a caller passing `defaultKeys` that don't match
     * any configured column.
     */
    private resolveDefaultColumns(
        overviewId: string,
        defaultKeys: string[],
        allColumns: ColumnDef<ApiFragment>[]
    ): ColumnDef<ApiFragment>[] {
        const defaultColumns: ColumnDef<ApiFragment>[] = this.keysToColumns(defaultKeys, allColumns);

        if (defaultColumns.length > 0) {
            return defaultColumns;
        }

        console.warn(
            `ColumnManagementService: none of the defaultKeys for "${overviewId}" match its allColumns; falling back to all configured columns.`
        );

        return allColumns;
    }

    /**
     * Puts back any pinned column the given selection is missing, at the position the catalog gives
     * it. Reachable from more than a misbehaving picker: a selection persisted before a column was
     * pinned can legitimately lack it. A selection that merely *reorders* the pinned columns is left
     * untouched — pinning fixes presence, not position.
     */
    private ensurePinnedKeys(
        keys: string[],
        allColumns: ColumnDef<ApiFragment>[],
        pinnedKeys: readonly string[]
    ): string[] {
        const catalogOrder: string[] = allColumns.map(column => column.toString());
        const missingKeys: string[] = pinnedKeys.filter(key => catalogOrder.includes(key) && !keys.includes(key));

        if (missingKeys.length === 0) {
            return keys;
        }

        const result: string[] = [...keys];

        for (const key of missingKeys) {
            const catalogIndex: number = catalogOrder.indexOf(key);
            const successorIndex: number = result.findIndex(existing => catalogOrder.indexOf(existing) > catalogIndex);

            result.splice(successorIndex === -1 ? result.length : successorIndex, 0, key);
        }

        return result;
    }

    private applyNameExclusivityGuard(
        selectedKeys: string[],
        previousKeys: string[],
        allColumns: ColumnDef<ApiFragment>[]
    ): string[] {
        let result: string[] = selectedKeys;

        for (const [keyA, keyB] of MUTUALLY_EXCLUSIVE_COLUMN_KEYS) {
            const bothConfigured: boolean =
                allColumns.some(column => column.toString() === keyA) &&
                allColumns.some(column => column.toString() === keyB);

            if (!bothConfigured || !result.includes(keyA) || !result.includes(keyB)) {
                continue;
            }

            const newlySelectedKey: string | undefined = result.find(
                key => (key === keyA || key === keyB) && !previousKeys.includes(key)
            );
            const keyToRemove: string = newlySelectedKey === keyA ? keyB : keyA;

            result = result.filter(key => key !== keyToRemove);
        }

        return result;
    }
}
